/* global process, isBrowser */

import { createStore, compose, applyMiddleware } from 'redux'
import { thunk } from 'redux-thunk'
import reduxImmutableStateInvariant from 'redux-immutable-state-invariant'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import _ from 'lodash'

import rootReducer from './modules/reducer'
import {
  sentryMiddleware,
  fetchFailureMiddleware,
  authFailureMiddleware,
  multiEffectsMiddleware,
  confirmationMiddleware
} from './middlewares'

// Migrate data from old redux-localstorage format
const migrateFromReduxLocalstorage = () => {
  if (typeof localStorage === 'undefined') return

  // Check if we have old format data and haven't migrated yet
  const oldAuth = localStorage.getItem('auth')
  const newPersistRoot = localStorage.getItem('persist:root')

  if (oldAuth && !newPersistRoot)
    try {
      // Old redux-localstorage stored each key directly
      const authData = JSON.parse(oldAuth)
      const checkoutData = localStorage.getItem('checkout')
        ? JSON.parse(localStorage.getItem('checkout'))
        : {}
      const hijackData = localStorage.getItem('hijack')
        ? JSON.parse(localStorage.getItem('hijack'))
        : {}

      // Create new redux-persist format
      const persistData = {
        auth: JSON.stringify(authData),
        checkout: JSON.stringify(checkoutData),
        hijack: JSON.stringify(hijackData),
        _persist: JSON.stringify({ version: 1, rehydrated: true })
      }

      localStorage.setItem('persist:root', JSON.stringify(persistData))

      // Clean up old keys
      localStorage.removeItem('auth')
      localStorage.removeItem('checkout')
      localStorage.removeItem('hijack')
    } catch (_) {
      // If migration fails, clear old data to start fresh
      localStorage.removeItem('auth')
      localStorage.removeItem('checkout')
      localStorage.removeItem('hijack')
    }
}

// Run migration before store creation
if (process.env.NODE_ENV !== 'test' && typeof localStorage !== 'undefined')
  migrateFromReduxLocalstorage()

// Redux-persist configuration
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'checkout', 'hijack'],
  version: 1
}

const persistedReducer =
  process.env.NODE_ENV !== 'test' && typeof localStorage !== 'undefined'
    ? persistReducer(persistConfig, rootReducer)
    : rootReducer

const baseMiddlewares = [
  thunk,
  fetchFailureMiddleware,
  authFailureMiddleware,
  multiEffectsMiddleware,
  confirmationMiddleware
]

function configureStoreProd() {
  const middlewares = [sentryMiddleware, ...baseMiddlewares]
  const store = createStore(
    persistedReducer,
    compose(applyMiddleware(...middlewares))
  )
  const persistor =
    process.env.NODE_ENV !== 'test' && typeof localStorage !== 'undefined'
      ? persistStore(store)
      : null
  return { store, persistor }
}

function configureStoreDev() {
  const middlewares = [reduxImmutableStateInvariant(), ...baseMiddlewares]

  // add support for Redux dev tools
  let composeEnhancers
  const extendedCompose = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  if (process.env.NODE_ENV !== 'test' && _.isFunction(extendedCompose))
    composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  else composeEnhancers = compose

  const store = createStore(
    persistedReducer,
    composeEnhancers(applyMiddleware(...middlewares))
  )

  const persistor =
    process.env.NODE_ENV !== 'test' && typeof localStorage !== 'undefined'
      ? persistStore(store)
      : null

  if (module.hot)
    // Enable Webpack hot module replacement for reducers
    module.hot.accept('./modules/reducer', () => {
      const nextReducer = require('./modules/reducer').default // eslint-disable-line global-require
      store.replaceReducer(
        process.env.NODE_ENV !== 'test' && typeof localStorage !== 'undefined'
          ? persistReducer(persistConfig, nextReducer)
          : nextReducer
      )
    })

  return { store, persistor }
}

const { store, persistor } =
  process.env.NODE_ENV === 'production' || !isBrowser
    ? configureStoreProd()
    : configureStoreDev()

export { persistor }
export default store
